<script setup lang="ts">
import { computed, ref, watch } from 'vue';

interface Props {
    data: Record<string, boolean | number | null>;
}

const props = defineProps<Props>();

const monthNames = [
    _mft('calendar:month.january.short'),
    _mft('calendar:month.february.short'),
    _mft('calendar:month.march.short'),
    _mft('calendar:month.april.short'),
    _mft('calendar:month.may.short'),
    _mft('calendar:month.june.short'),
    _mft('calendar:month.july.short'),
    _mft('calendar:month.august.short'),
    _mft('calendar:month.september.short'),
    _mft('calendar:month.october.short'),
    _mft('calendar:month.november.short'),
    _mft('calendar:month.december.short'),
];

const dayLabels = [_mft('calendar:weekday.monday.short'), '', _mft('calendar:weekday.wednesday.short'), '', _mft('calendar:weekday.friday.short'), '', _mft('calendar:weekday.sunday.short')];
const colorScale = ['#dddddd', '#bbb', '#999', '#777', '#555'];

const allResponseDates = computed(() => Object.keys(props.data || {}));

const responseCountsByDate = computed(() => {
    const counts: Record<string, number> = {};
    Object.entries(props.data || {}).forEach(([date, value]) => {
        if (!value) {
            return;
        }

        counts[date] = 1;
    });

    return counts;
});

const availableYears = computed(() => {
    const years = new Set<number>();

    allResponseDates.value.forEach((date) => {
        const year = Number(date.slice(0, 4));
        if (!Number.isNaN(year)) {
            years.add(year);
        }
    });

    const sortedYears = Array.from(years).sort((a, b) => b - a);
    return sortedYears.length > 0 ? sortedYears : [new Date().getUTCFullYear()];
});

const selectedYear = ref(availableYears.value[0]);

watch(availableYears, (years) => {
    if (!years.includes(selectedYear.value)) {
        selectedYear.value = years[0];
    }
}, { immediate: true });

const makeUTCDate = (year: number, monthIndex: number, day: number) => {
    return new Date(Date.UTC(year, monthIndex, day));
};

const parseIsoDate = (date: string) => {
    const [year, month, day] = date.split('-').map((part) => Number(part));
    return makeUTCDate(year, month - 1, day);
};

const formatIsoDate = (date: Date) => date.toISOString().slice(0, 10);

const maxResponsesInYear = computed(() => {
    const counts = responseCountsByDate.value;
    const year = selectedYear.value;

    let max = 0;
    Object.entries(counts).forEach(([date, count]) => {
        if (Number(date.slice(0, 4)) === year && count > max) {
            max = count;
        }
    });

    return Math.max(1, max);
});

const weeks = computed(() => {
    const year = selectedYear.value;
    const yearStart = makeUTCDate(year, 0, 1);
    const yearEnd = makeUTCDate(year, 11, 31);

    const start = new Date(yearStart);
    const startOffset = (start.getUTCDay() + 6) % 7;
    start.setUTCDate(start.getUTCDate() - startOffset);

    const end = new Date(yearEnd);
    const endOffset = (7 - ((end.getUTCDay() + 6) % 7)) % 7;
    end.setUTCDate(end.getUTCDate() + endOffset);

    const days: Date[] = [];
    const cursor = new Date(start);

    while (cursor <= end) {
        days.push(new Date(cursor));
        cursor.setUTCDate(cursor.getUTCDate() + 1);
    }

    const result: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) {
        result.push(days.slice(i, i + 7));
    }

    return result;
});

const monthLabels = computed(() => {
    const year = selectedYear.value;

    return weeks.value.map((week) => {
        const firstOfMonth = week.find((day) => day.getUTCDate() === 1);
        if (!firstOfMonth || firstOfMonth.getUTCFullYear() !== year) {
            return '';
        }

        return monthNames[firstOfMonth.getUTCMonth()];
    });
});

const gridDays = computed(() => {
    const year = selectedYear.value;
    const counts = responseCountsByDate.value;

    return weeks.value.flatMap((week) => {
        return week.map((date) => {
            const iso = formatIsoDate(date);
            const inYear = date.getUTCFullYear() === year;
            const count = inYear ? (counts[iso] || 0) : 0;

            return {
                iso,
                inYear,
                count,
            };
        });
    });
});

const gridStyle = computed(() => ({
    gridTemplateRows: 'repeat(7, var(--cell-size))',
    gridAutoFlow: 'column',
    gridAutoColumns: 'var(--cell-size)',
}));

const monthGridStyle = computed(() => ({
    gridTemplateColumns: `repeat(${weeks.value.length}, var(--cell-size))`,
}));

const getCellColor = (count: number, inYear: boolean) => {
    if (!inYear) {
        return 'rgba(255,255,255,0)';
    }

    if (count === 0) {
        return colorScale[0];
    }

    const max = maxResponsesInYear.value;
    const level = Math.ceil((count / max) * 4);
    return colorScale[Math.min(level, 4)];
};

const cellTitle = (day: { iso: string; count: number; inYear: boolean }) => {
    if (!day.inYear) {
        return '';
    }

    return day.iso;
};
</script>

<template>
    <div class="">
        <div class="checkins-chart">
            <div class="checkins-chart__header">
                <div>
                    <slot name="header"></slot>
                </div>
                <select
                    v-model="selectedYear"
                    class="text-sm border border-gray-200 rounded px-2 py-1 bg-white"
                >
                    <option v-for="year in availableYears" :key="year" :value="year">
                        {{ year }}
                    </option>
                </select>
            </div>
            <div v-if="gridDays.length === 0" class="text-sm text-gray-500 text-center my-4 pb-4">
                No data available.
            </div>
            <div v-else class="checkins-chart__body max-w-full overflow-x-auto pb-4">
                <div class="checkins-chart__labels">
                    <div
                        v-for="label in dayLabels"
                        :key="label"
                        class="checkins-chart__day-label"
                    >
                        {{ label }}
                    </div>
                </div>
                <div class="checkins-chart__grid-wrapper">
                    <div class="checkins-chart__months" :style="monthGridStyle">
                        <div
                            v-for="(label, index) in monthLabels"
                            :key="`month-${index}`"
                            class="checkins-chart__month-label"
                        >
                            {{ label }}
                        </div>
                    </div>
                    <div class="checkins-chart__grid" :style="gridStyle">
                        <div
                            v-for="day in gridDays"
                            :key="day.iso"
                            class="checkins-chart__cell"
                            :style="{ backgroundColor: getCellColor(day.count, day.inYear) }"
                            :title="cellTitle(day)"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.checkins-chart {
    --cell-size: 12px;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.checkins-chart__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
}

.checkins-chart__body {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
}

.checkins-chart__labels {
    display: grid;
    grid-template-rows: repeat(7, var(--cell-size));
    gap: 3px;
    text-align: right;
    width: 2.25rem;
    padding-top: 20px;
}

.checkins-chart__day-label {
    font-size: 0.65rem;
    color: #9ca3af;
    line-height: var(--cell-size);
}

.checkins-chart__grid-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    min-width: 680px;
}

.checkins-chart__months {
    display: grid;
    gap: 3px;
    font-size: 0.65rem;
    color: #9ca3af;
    min-height: 0.8rem;
}

.checkins-chart__month-label {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    line-height: 0.8rem;
}

.checkins-chart__grid {
    display: grid;
    gap: 3px;
}

.checkins-chart__cell {
    width: var(--cell-size);
    height: var(--cell-size);
    border-radius: 2px;
}

.checkins-chart__legend {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    padding-top: 0.2rem;
}

.checkins-chart__legend-scale {
    display: flex;
    gap: 3px;
}

.checkins-chart__legend-cell {
    width: var(--cell-size);
    height: var(--cell-size);
    border-radius: 2px;
}

@media (max-width: 768px) {
    .checkins-chart__grid-wrapper {
        min-width: 520px;
    }
}
</style>
